import React, { useEffect, useId, useRef, useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { customFieldService } from '../../services/custom-field.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { CustomField } from '../../types'
import './CustomFieldsQueryDropdown.scss'
import { DocumentLinkInput } from './DocumentLinkInput'

// Custom field query operators (matching React)
export enum CustomFieldQueryOperator {
  Exact = 'exact',
  In = 'in',
  IsNull = 'isnull',
  Exists = 'exists',
  Contains = 'contains',
  IContains = 'icontains',
  GreaterThan = 'gt',
  GreaterThanOrEqual = 'gte',
  LessThan = 'lt',
  LessThanOrEqual = 'lte',
  Range = 'range',
}

export enum CustomFieldQueryLogicalOperator {
  And = 'AND',
  Or = 'OR',
  Not = 'NOT',
}

export enum CustomFieldQueryElementType {
  Atom = 'Atom',
  Expression = 'Expression',
}

// Custom field data types
export enum CustomFieldDataType {
  Boolean = 'boolean',
  Date = 'date',
  Integer = 'integer',
  Float = 'float',
  Monetary = 'monetary',
  String = 'string',
  Url = 'url',
  DocumentLink = 'documentlink',
  Select = 'select',
  LongText = 'longtext',
  Counter = 'counter',
}

// Query element interfaces
export interface CustomFieldQueryAtom {
  field: number | null
  operator: string
  value: any
}

export interface CustomFieldQueryExpression {
  operator: string
  elements: CustomFieldQueryElement[]
}

export interface CustomFieldQueryElement {
  type: CustomFieldQueryElementType
  atom?: CustomFieldQueryAtom
  expression?: CustomFieldQueryExpression
}

// Selection model for custom field queries
export class CustomFieldQueriesModel {
  public queries: CustomFieldQueryElement[] = []
  public logicalOperator: 'OR' | 'AND' | 'NOT' = 'OR' // Default to OR (Bilo koja)
  public changed = new (class {
    private listeners: (() => void)[] = []
    subscribe(callback: () => void) {
      this.listeners.push(callback)
    }
    next() {
      this.listeners.forEach((callback) => callback())
    }
  })()

  addQuery(query: CustomFieldQueryElement) {
    this.queries.push(query)
    this.changed.next()
  }

  addExpression(expression?: CustomFieldQueryElement) {
    const newExpression: CustomFieldQueryElement = expression || {
      type: CustomFieldQueryElementType.Expression,
      expression: {
        operator: 'OR',
        elements: [],
      },
    }
    this.queries.push(newExpression)
    this.changed.next()
  }

  removeQuery(index: number) {
    this.queries.splice(index, 1)
    this.changed.next()
  }

  clear() {
    this.queries = []
    this.changed.next()
  }

  isValid(): boolean {
    if (this.queries.length === 0) return false

    const validateAtom = (atom: CustomFieldQueryAtom): boolean => {
      if (!atom.field || !atom.operator || atom.value === null || atom.value === undefined) {
        return false
      }

      if (atom.operator === CustomFieldQueryOperator.Exists || atom.operator === CustomFieldQueryOperator.IsNull) {
        return true
      }

      if ([CustomFieldQueryOperator.In, CustomFieldQueryOperator.Contains].includes(atom.operator as CustomFieldQueryOperator)) {
        return Array.isArray(atom.value) && atom.value.length > 0
      }

      if (typeof atom.value === 'string' && atom.value.trim() === '') {
        return false
      }

      return true
    }

    const validateExpression = (expression: CustomFieldQueryExpression): boolean => {
      if (!expression.operator || !expression.elements || expression.elements.length === 0) {
        return false
      }

      return expression.elements.every((e) => {
        if (e.type === CustomFieldQueryElementType.Atom && e.atom) {
          return validateAtom(e.atom)
        } else if (e.type === CustomFieldQueryElementType.Expression && e.expression) {
          return validateExpression(e.expression)
        }
        return false
      })
    }

    return this.queries.every((query) => {
      if (query.type === CustomFieldQueryElementType.Atom && query.atom) {
        return validateAtom(query.atom)
      } else if (query.type === CustomFieldQueryElementType.Expression && query.expression) {
        return validateExpression(query.expression)
      }
      return false
    })
  }

  isEmpty(): boolean {
    if (this.queries.length === 0) {
      return true
    }

    if (this.queries.length === 1 && this.queries[0].type === CustomFieldQueryElementType.Expression && this.queries[0].expression) {
      return this.queries[0].expression.elements.length === 0
    }

    return false
  }

  serialize(): string {
    if (this.queries.length === 0) return ''

    const serializeElement = (element: CustomFieldQueryElement): any => {
      if (element.type === CustomFieldQueryElementType.Atom && element.atom) {
        return [element.atom.field, element.atom.operator, element.atom.value]
      } else if (element.type === CustomFieldQueryElementType.Expression && element.expression) {
        const elements = element.expression.elements.map((el) => serializeElement(el))
        let value = elements
        if (element.expression.operator === CustomFieldQueryLogicalOperator.Not && value.length === 1) {
          value = value[0]
        }
        return [element.expression.operator, value]
      }
      return null
    }

    if (this.queries.length === 1 && this.queries[0].type === CustomFieldQueryElementType.Atom && this.queries[0].atom) {
      const atom = this.queries[0].atom
      return JSON.stringify([this.logicalOperator, [[atom.field, atom.operator, atom.value]]])
    }

    if (this.queries.length === 1 && this.queries[0].type === CustomFieldQueryElementType.Expression && this.queries[0].expression) {
      const expression = this.queries[0].expression
      const elements = expression.elements.map((el) => serializeElement(el))
      let value = elements
      if (expression.operator === CustomFieldQueryLogicalOperator.Not && value.length === 1) {
        value = value[0]
      }
      return JSON.stringify([expression.operator, value])
    }

    if (this.queries.length > 1) {
      const serialized = this.queries.map((query) => serializeElement(query)).filter(Boolean)
      if (serialized.length === 0) return ''
      return JSON.stringify([this.logicalOperator, serialized])
    }

    const serialized = this.queries.map((query) => serializeElement(query)).filter(Boolean)

    if (serialized.length === 0) return ''

    return JSON.stringify(serialized[0])
  }
}

interface CustomFieldsQueryDropdownProps {
  title?: string
  icon?: string
  disabled?: boolean
  className?: string
  selectionModel: CustomFieldQueriesModel
  onSelectionModelChange: (model: CustomFieldQueriesModel) => void
  fullWidth?: boolean
  useDropdown?: boolean
  /** When set, only these custom field IDs appear in the picker. */
  allowedCustomFieldIds?: number[]
}

const CUSTOM_FIELD_QUERY_MAX_DEPTH = 4
const CUSTOM_FIELD_QUERY_MAX_ATOMS = 5

const CustomFieldsQueryDropdown: React.FC<CustomFieldsQueryDropdownProps> = ({
  title = '',
  icon = '',
  disabled = false,
  className = '',
  selectionModel,
  onSelectionModelChange,
  fullWidth = true,
  useDropdown = true,
  allowedCustomFieldIds,
}) => {
  const { t } = useTranslation()
  const instanceId = useId()
  const [customFields, setCustomFields] = useState<CustomField[]>([])
  const [show, setShow] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [localModel, setLocalModel] = useState<CustomFieldQueriesModel>(() => {
    const model = new CustomFieldQueriesModel()
    model.logicalOperator = selectionModel.logicalOperator
    model.queries = [...selectionModel.queries]
    return model
  })
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isEditingRef = useRef(false)
  const localModelRef = useRef<CustomFieldQueriesModel>(localModel)
  const initialModelRef = useRef(selectionModel)
  const lastSentQueryRef = useRef<string>('')
  const isCallingBackendRef = useRef(false)
  const [menuContainer, setMenuContainer] = useState<HTMLElement | null>(null)
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 420 })
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < 992
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(max-width: 991.98px)')
    const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
      setIsMobileViewport(event.matches)
    }

    handleChange(mediaQuery)

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange)
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange)
      } else if (mediaQuery.removeListener) {
        mediaQuery.removeListener(handleChange)
      }
    }
  }, [])
  useEffect(() => {
    if (typeof document !== 'undefined') {
      setMenuContainer(document.body)
    }
  }, [])

  const updateMenuPosition = () => {
    if (typeof window === 'undefined' || !dropdownRef.current) return
    const rect = dropdownRef.current.getBoundingClientRect()
    const margin = 16
    const maxWidth = 420
    const availableWidth = Math.max(320, Math.min(maxWidth, window.innerWidth - margin * 2))

    if (isMobileViewport) {
      const spacing = 12
      const effectiveWidth = Math.min(availableWidth, window.innerWidth - margin * 2)
      let left = rect.left + window.scrollX
      left = Math.max(margin, Math.min(left, window.innerWidth - effectiveWidth - margin))
      const top = rect.bottom + window.scrollY + spacing

      setMenuPosition({
        top,
        left,
        width: effectiveWidth,
      })
      return
    }

    let left = rect.right + 12

    if (left + availableWidth > window.innerWidth - margin) {
      left = rect.left - availableWidth - 12
    }
    if (left < margin) {
      left = margin
    }
    if (left + availableWidth > window.innerWidth - margin) {
      left = window.innerWidth - availableWidth - margin
    }

    const top = rect.top + window.scrollY

    setMenuPosition({
      top,
      left: left + window.scrollX,
      width: availableWidth,
    })
  }

  useEffect(() => {
    if (!show) return
    updateMenuPosition()
    const handleUpdate = () => updateMenuPosition()
    window.addEventListener('resize', handleUpdate)
    window.addEventListener('scroll', handleUpdate, true)
    return () => {
      window.removeEventListener('resize', handleUpdate)
      window.removeEventListener('scroll', handleUpdate, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, isMobileViewport])

  useEffect(() => {
    if (!isEditingRef.current && !show) {
      const newModel = new CustomFieldQueriesModel()
      newModel.logicalOperator = selectionModel.logicalOperator
      newModel.queries = [...selectionModel.queries]
      setLocalModel(newModel)
      localModelRef.current = newModel
      initialModelRef.current = selectionModel
    }
  }, [selectionModel, show])

  useEffect(() => {
    if (!useDropdown && localModel.queries.length === 0 && customFields.length > 0 && !isEditingRef.current) {
      const newExpression: CustomFieldQueryElement = {
        type: CustomFieldQueryElementType.Expression,
        expression: {
          operator: localModel.logicalOperator,
          elements: [],
        },
      }
      const newModel = new CustomFieldQueriesModel()
      newModel.logicalOperator = localModel.logicalOperator
      newModel.queries = [newExpression]
      setLocalModel(newModel)
      localModelRef.current = newModel
    }
  }, [useDropdown, customFields.length, localModel.queries.length])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if ((dropdownRef.current && dropdownRef.current.contains(target)) || (menuRef.current && menuRef.current.contains(target))) {
        return
      }
      if (dropdownRef.current) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
          debounceTimerRef.current = null
        }
        const currentModel = localModelRef.current
        if (currentModel.isEmpty() || currentModel.isValid()) {
          isEditingRef.current = false
          onSelectionModelChange(currentModel)
        }
        setShow(false)
      }
    }

    if (show) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
    }
  }, [show])

  // Load custom fields
  useEffect(() => {
    const loadCustomFields = async () => {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.CustomField)) {
        setCustomFields([])
        return
      }
      try {
        const response = await customFieldService.listAll()
        let fields = (response.results || []).filter((f) => f.data_type !== 'separator')
        if (allowedCustomFieldIds && allowedCustomFieldIds.length > 0) {
          const allowed = new Set(allowedCustomFieldIds)
          fields = fields.filter((f) => allowed.has(f.id))
        }
        setCustomFields(fields)
      } catch (error) {}
    }
    loadCustomFields()
  }, [allowedCustomFieldIds])

  const isActive = localModel.queries.length > 0 && localModel.isValid()

  // Duboka kopija modela: parent ne sme deliti reference sa dropdown-om (mutacije bi curile).
  const cloneModelForParent = (source: CustomFieldQueriesModel): CustomFieldQueriesModel => {
    const clone = new CustomFieldQueriesModel()
    clone.logicalOperator = source.logicalOperator
    clone.queries = source.queries.map((q) => {
      if (q.type === CustomFieldQueryElementType.Atom && q.atom) {
        return {
          type: CustomFieldQueryElementType.Atom,
          atom: { ...q.atom },
        }
      } else if (q.type === CustomFieldQueryElementType.Expression && q.expression) {
        return {
          type: CustomFieldQueryElementType.Expression,
          expression: {
            operator: q.expression.operator,
            elements: q.expression.elements.map((el) => {
              if (el.type === CustomFieldQueryElementType.Atom && el.atom) {
                return {
                  type: CustomFieldQueryElementType.Atom,
                  atom: { ...el.atom },
                }
              }
              return el
            }),
          },
        }
      }
      return q
    })
    return clone
  }

  const handleModelChange = (model: CustomFieldQueriesModel, immediate: boolean = false) => {
    const newModel = cloneModelForParent(model)

    setLocalModel(newModel)
    localModelRef.current = newModel

    // Inline (useDropdown=false) je form-editing kontekst: emitujemo odmah, bez debounce-a
    // i bez „empty guard"-a — inače brzi Save gubi izmenu, a čišćenje uslova ne stiže parent-u.
    const isFormEditingMode = !useDropdown

    if (isFormEditingMode) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      const serializedQuery = newModel.serialize()
      lastSentQueryRef.current = serializedQuery
      onSelectionModelChange(cloneModelForParent(newModel))
      return
    }

    if (newModel.isEmpty() || newModel.isValid()) {
      const serializedQuery = newModel.serialize()

      if (serializedQuery === lastSentQueryRef.current || serializedQuery === '') {
        return
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }

      const callBackend = () => {
        if (isCallingBackendRef.current) {
          return
        }

        const currentSerialized = localModelRef.current.serialize()
        if (currentSerialized === lastSentQueryRef.current || currentSerialized === '') {
          return
        }

        isCallingBackendRef.current = true
        lastSentQueryRef.current = currentSerialized

        onSelectionModelChange(cloneModelForParent(localModelRef.current))

        setTimeout(() => {
          isCallingBackendRef.current = false
        }, 500)
      }

      if (immediate || newModel.isEmpty()) {
        callBackend()
      } else {
        debounceTimerRef.current = setTimeout(callBackend, 1000)
      }
    }
  }

  const handleAddQuery = () => {
    const currentModel = localModelRef.current

    if (currentModel.queries.length === 0) {
      const newExpression: CustomFieldQueryElement = {
        type: CustomFieldQueryElementType.Expression,
        expression: {
          operator: currentModel.logicalOperator,
          elements: [],
        },
      }
      const newModel = new CustomFieldQueriesModel()
      newModel.logicalOperator = currentModel.logicalOperator
      newModel.queries = [newExpression]
      setLocalModel(newModel)
      localModelRef.current = newModel
      return
    }

    const newAtom: CustomFieldQueryAtom = {
      field: null,
      operator: CustomFieldQueryOperator.Exists,
      value: 'true',
    }

    const newQuery: CustomFieldQueryElement = {
      type: CustomFieldQueryElementType.Atom,
      atom: newAtom,
    }

    const newModel = new CustomFieldQueriesModel()
    newModel.logicalOperator = currentModel.logicalOperator
    newModel.queries = currentModel.queries.map((q) => {
      if (q.type === CustomFieldQueryElementType.Expression && q.expression) {
        return {
          type: CustomFieldQueryElementType.Expression,
          expression: {
            operator: q.expression.operator,
            elements: [...q.expression.elements, newQuery],
          },
        }
      }
      return q
    })

    if (newModel.queries.length === 0 || (newModel.queries.length > 0 && newModel.queries[0].type !== CustomFieldQueryElementType.Expression)) {
      const newExpression: CustomFieldQueryElement = {
        type: CustomFieldQueryElementType.Expression,
        expression: {
          operator: currentModel.logicalOperator,
          elements: [newQuery],
        },
      }
      newModel.queries = [newExpression, ...newModel.queries]
    }

    handleModelChange(newModel)
  }

  const getOperatorsForField = (fieldId: number) => {
    const field = customFields.find((f) => f.id === fieldId)
    if (!field) return []

    const operators = [CustomFieldQueryOperator.Exists, CustomFieldQueryOperator.IsNull]

    switch (field.data_type) {
      case CustomFieldDataType.String:
      case CustomFieldDataType.Url:
        operators.push(CustomFieldQueryOperator.Exact, CustomFieldQueryOperator.IContains)
        break
      case CustomFieldDataType.Date:
        operators.push(CustomFieldQueryOperator.Exact, CustomFieldQueryOperator.GreaterThanOrEqual, CustomFieldQueryOperator.LessThanOrEqual)
        break
      case CustomFieldDataType.Boolean:
        operators.push(CustomFieldQueryOperator.Exact)
        break
      case CustomFieldDataType.Integer:
      case CustomFieldDataType.Float:
      case CustomFieldDataType.Monetary:
        operators.push(
          CustomFieldQueryOperator.Exact,
          CustomFieldQueryOperator.GreaterThan,
          CustomFieldQueryOperator.GreaterThanOrEqual,
          CustomFieldQueryOperator.LessThan,
          CustomFieldQueryOperator.LessThanOrEqual
        )
        break
      case CustomFieldDataType.Select:
        operators.push(CustomFieldQueryOperator.Exact, CustomFieldQueryOperator.In)
        break
      case CustomFieldDataType.DocumentLink:
        operators.push(CustomFieldQueryOperator.Contains)
        break
      case CustomFieldDataType.LongText:
        operators.push(CustomFieldQueryOperator.IContains)
        break
      case CustomFieldDataType.Counter:
        operators.push(CustomFieldQueryOperator.Exact, CustomFieldQueryOperator.IContains)
        break
    }

    return operators
  }

  const getOperatorLabel = (operator: string) => {
    const labels: { [key: string]: string } = {
      [CustomFieldQueryOperator.Exact]: t('equal_to'),
      [CustomFieldQueryOperator.In]: t('in'),
      [CustomFieldQueryOperator.IsNull]: t('is_null'),
      [CustomFieldQueryOperator.Exists]: t('exists'),
      [CustomFieldQueryOperator.Contains]: t('contains'),
      [CustomFieldQueryOperator.IContains]: t('contains_case_insensitive'),
      [CustomFieldQueryOperator.GreaterThan]: t('greater_than'),
      [CustomFieldQueryOperator.GreaterThanOrEqual]: t('greater_than_or_equal'),
      [CustomFieldQueryOperator.LessThan]: t('less_than'),
      [CustomFieldQueryOperator.LessThanOrEqual]: t('less_than_or_equal'),
      [CustomFieldQueryOperator.Range]: t('range'),
    }
    return labels[operator] || operator
  }

  const renderExpressionGroup = (expression: CustomFieldQueryExpression, depth: number = 0, expressionId?: string) => {
    // `expressionUniqueId` koristimo interno (rootIndex/elementIndex parsing). Za DOM atribute
    // (`name`, `id`) MORAMO da uključimo `instanceId` — inače sibling dropdown-ovi dele istu
    // radio-grupu i selekcija u jednom pomera selekciju u drugom.
    const expressionUniqueId = expressionId || `expression_${depth}`
    const domScopeId = `${instanceId}_${expressionUniqueId}`
    const canAddAtom = expression.elements.length < CUSTOM_FIELD_QUERY_MAX_ATOMS
    const canAddExpression = depth < CUSTOM_FIELD_QUERY_MAX_DEPTH
    const isNegatable = expression.elements.length === 1 && expression.elements[0]?.type === CustomFieldQueryElementType.Expression
    const isOperatorDisabled = depth > 0 && expression.elements.length < 2

    const removeNestedExpression = () => {
      const parts = expressionUniqueId.split('_')

      if (depth === 0) {
        const rootIndex = parseInt(parts[1] || '0')
        const newModel = new CustomFieldQueriesModel()
        newModel.logicalOperator = localModel.logicalOperator
        newModel.queries = localModel.queries.filter((_, index) => index !== rootIndex)
        handleModelChange(newModel)
        return
      }

      if (parts.length < 3) {
        return
      }

      const rootIndex = parseInt(parts[1] || '0')
      const elementIndex = parseInt(parts[parts.length - 1] || '0')

      const newModel = new CustomFieldQueriesModel()
      newModel.logicalOperator = localModel.logicalOperator
      newModel.queries = localModel.queries.map((q, index) => {
        if (index === rootIndex && q.type === CustomFieldQueryElementType.Expression && q.expression) {
          const removeFromElements = (
            elements: CustomFieldQueryElement[],
            targetDepth: number,
            targetIndex: number,
            currentDepth: number
          ): CustomFieldQueryElement[] => {
            if (currentDepth === targetDepth) {
              return elements.filter((_, i) => i !== targetIndex)
            }
            return elements.map((el) => {
              if (el.type === CustomFieldQueryElementType.Expression && el.expression) {
                return {
                  type: CustomFieldQueryElementType.Expression,
                  expression: {
                    operator: el.expression.operator,
                    elements: removeFromElements(el.expression.elements, targetDepth, targetIndex, currentDepth + 1),
                  },
                }
              }
              return el
            })
          }

          return {
            type: CustomFieldQueryElementType.Expression,
            expression: {
              operator: q.expression.operator,
              elements: removeFromElements(q.expression.elements, depth - 1, elementIndex, 0),
            },
          }
        }
        return q
      })
      handleModelChange(newModel)
    }

    const updateExpressionInModel = (updatedExpression: CustomFieldQueryExpression) => {
      const parts = expressionUniqueId.split('_')
      const rootIndex = parseInt(parts[1] || '0')

      if (depth === 0) {
        const newModel = new CustomFieldQueriesModel()
        newModel.logicalOperator = localModel.logicalOperator
        newModel.queries = localModel.queries.map((q, index) => {
          if (index === rootIndex && q.type === CustomFieldQueryElementType.Expression && q.expression) {
            return {
              type: CustomFieldQueryElementType.Expression,
              expression: updatedExpression,
            }
          }
          return q
        })
        handleModelChange(newModel)
        return
      }

      const updateInElements = (elements: CustomFieldQueryElement[], targetDepth: number, currentDepth: number): CustomFieldQueryElement[] => {
        if (currentDepth === targetDepth) {
          return elements.map((el) => {
            if (el.type === CustomFieldQueryElementType.Expression && el.expression) {
              const elId = `${expressionUniqueId}_${elements.indexOf(el)}`
              if (elId === expressionUniqueId || JSON.stringify(el.expression) === JSON.stringify(expression)) {
                return {
                  type: CustomFieldQueryElementType.Expression,
                  expression: updatedExpression,
                }
              }
            }
            return el
          })
        }
        return elements.map((el) => {
          if (el.type === CustomFieldQueryElementType.Expression && el.expression) {
            return {
              type: CustomFieldQueryElementType.Expression,
              expression: {
                operator: el.expression.operator,
                elements: updateInElements(el.expression.elements, targetDepth, currentDepth + 1),
              },
            }
          }
          return el
        })
      }

      const newModel = new CustomFieldQueriesModel()
      newModel.logicalOperator = localModel.logicalOperator
      newModel.queries = localModel.queries.map((q, index) => {
        if (index === rootIndex && q.type === CustomFieldQueryElementType.Expression && q.expression) {
          return {
            type: CustomFieldQueryElementType.Expression,
            expression: {
              operator: q.expression.operator,
              elements: updateInElements(q.expression.elements, depth - 1, 0),
            },
          }
        }
        return q
      })
      handleModelChange(newModel)
    }

    const addAtomToExpression = () => {
      if (!expression.elements) expression.elements = []

      const newAtom: CustomFieldQueryAtom = {
        field: null,
        operator: CustomFieldQueryOperator.Exists,
        value: 'true',
      }

      const newElements = [
        ...expression.elements,
        {
          type: CustomFieldQueryElementType.Atom,
          atom: newAtom,
        },
      ]

      updateExpressionInModel({
        operator: expression.operator,
        elements: newElements,
      })
    }

    const addExpressionToExpression = () => {
      if (!expression.elements) expression.elements = []
      const newElements = [
        ...expression.elements,
        {
          type: CustomFieldQueryElementType.Expression,
          expression: {
            operator: 'OR',
            elements: [],
          },
        },
      ]

      updateExpressionInModel({
        operator: expression.operator,
        elements: newElements,
      })
    }

    const removeElementFromExpression = (elementIndex: number) => {
      const newElements = expression.elements.filter((_, i) => i !== elementIndex)

      updateExpressionInModel({
        operator: expression.operator,
        elements: newElements,
      })
    }

    const updateExpressionOperator = (newOperator: 'OR' | 'AND' | 'NOT') => {
      expression.operator = newOperator

      const newModel = new CustomFieldQueriesModel()
      newModel.logicalOperator = localModel.logicalOperator
      newModel.queries = localModel.queries.map((q) => {
        if (q.type === CustomFieldQueryElementType.Expression && q.expression === expression) {
          return {
            type: CustomFieldQueryElementType.Expression,
            expression: {
              operator: newOperator,
              elements: [...expression.elements],
            },
          }
        }
        return q
      })
      handleModelChange(newModel)
    }

    const handleAtomChange = (elementIndex: number, field: string, value: any) => {
      const element = expression.elements[elementIndex]
      if (element.type === CustomFieldQueryElementType.Atom && element.atom) {
        const atom = element.atom
        const currentField = customFields.find((f) => f.id === atom.field)

        if (field === 'field') {
          atom.field = value
          const newField = customFields.find((f) => f.id === value)
          if (newField?.data_type === CustomFieldDataType.DocumentLink && !Array.isArray(atom.value)) {
            atom.value = []
          }
        }
        if (field === 'operator') {
          atom.operator = value
          if (value === CustomFieldQueryOperator.Exists || value === CustomFieldQueryOperator.IsNull) {
            atom.value = 'true'
          } else if (value === CustomFieldQueryOperator.In) {
            atom.value = []
          } else if (currentField?.data_type === CustomFieldDataType.DocumentLink) {
            atom.value = []
          } else {
            atom.value = ''
          }
        }
        if (field === 'value') {
          atom.value = value
        }

        const updatedModel = new CustomFieldQueriesModel()
        updatedModel.logicalOperator = localModel.logicalOperator
        updatedModel.queries = localModel.queries.map((q) => {
          if (q.type === CustomFieldQueryElementType.Expression && q.expression === expression) {
            return {
              type: CustomFieldQueryElementType.Expression,
              expression: {
                operator: expression.operator,
                elements: [...expression.elements],
              },
            }
          }
          return q
        })

        const wasValid = localModel.isValid()
        const isValid = updatedModel.isValid()

        handleModelChange(updatedModel, isValid && !wasValid)
      }
    }

    return (
      <div className="d-flex w-100">
        <div className="d-flex flex-grow-1 flex-column">
          <div className="btn-group btn-group-xs" role="group">
            <input
              type="radio"
              className="btn-check"
              id={`logicalOperatorOr_${domScopeId}`}
              name={`logicalOperator_${domScopeId}`}
              value="OR"
              checked={expression.operator === 'OR'}
              onChange={() => updateExpressionOperator('OR')}
              disabled={isOperatorDisabled || disabled}
            />
            <label className="btn btn-outline-primary" htmlFor={`logicalOperatorOr_${domScopeId}`}>
              {t('any')}
            </label>
            <input
              type="radio"
              className="btn-check"
              id={`logicalOperatorAnd_${domScopeId}`}
              name={`logicalOperator_${domScopeId}`}
              value="AND"
              checked={expression.operator === 'AND'}
              onChange={() => updateExpressionOperator('AND')}
              disabled={isOperatorDisabled || disabled}
            />
            <label className="btn btn-outline-primary" htmlFor={`logicalOperatorAnd_${domScopeId}`}>
              {t('all')}
            </label>
            {isNegatable && (
              <>
                <input
                  type="radio"
                  className="btn-check"
                  id={`logicalOperatorNot_${domScopeId}`}
                  name={`logicalOperator_${domScopeId}`}
                  value="NOT"
                  checked={expression.operator === 'NOT'}
                  onChange={() => updateExpressionOperator('NOT')}
                  disabled={disabled}
                />
                <label className="btn btn-outline-secondary" htmlFor={`logicalOperatorNot_${domScopeId}`}>
                  {t('not')}
                </label>
              </>
            )}
          </div>
          <div className="list-group list-group-flush mb-n2">
            {expression.elements.map((element, elementIndex) => (
              <div key={elementIndex} className="list-group-item px-0 d-flex flex-nowrap">
                {element.type === CustomFieldQueryElementType.Atom && element.atom ? (
                  <div className="input-group input-group-sm w-100">
                    <Form.Select
                      className="docdot-input-select"
                      value={element.atom.field || ''}
                      onChange={(e) => handleAtomChange(elementIndex, 'field', parseInt(e.target.value))}
                      onMouseDown={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <option value="">{t('select_field')}</option>
                      {customFields.map((field) => (
                        <option key={field.id} value={field.id}>
                          {field.name}
                        </option>
                      ))}
                    </Form.Select>

                    <Form.Select
                      className="w-25"
                      value={element.atom.operator || ''}
                      onChange={(e) => handleAtomChange(elementIndex, 'operator', e.target.value)}
                      disabled={disabled}
                    >
                      {getOperatorsForField(element.atom.field || 0).map((operator) => (
                        <option key={operator} value={operator}>
                          {getOperatorLabel(operator)}
                        </option>
                      ))}
                    </Form.Select>

                    {renderAtomValueInput(element.atom, (val) => handleAtomChange(elementIndex, 'value', val))}

                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger pe-0"
                      type="button"
                      onClick={() => removeElementFromExpression(elementIndex)}
                      disabled={disabled}
                    >
                      <i className="bi bi-x-circle"></i>
                    </Button>
                  </div>
                ) : element.type === CustomFieldQueryElementType.Expression && element.expression ? (
                  <div className="w-100">{renderExpressionGroup(element.expression, depth + 1, `${expressionUniqueId}_${elementIndex}`)}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="btn-group-vertical ms-2 ps-2 border-start" role="group" aria-label="Vertical button group">
          <Button
            variant="outline-secondary"
            size="sm"
            className="text-primary"
            title={t('add_query')}
            onClick={addAtomToExpression}
            disabled={disabled || !canAddAtom}
          >
            <i className="bi bi-node-plus"></i>
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            className="text-primary"
            title={t('add_expression')}
            onClick={addExpressionToExpression}
            disabled={disabled || !canAddExpression}
          >
            <i className="bi bi-braces"></i>
          </Button>
          {depth > 0 && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="text-danger"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                removeNestedExpression()
              }}
              disabled={disabled}
            >
              <i className="bi bi-x-circle"></i>
            </Button>
          )}
        </div>
      </div>
    )
  }

  const renderAtomValueInput = (atom: CustomFieldQueryAtom, onValueChange: (value: any) => void) => {
    const field = customFields.find((f) => f.id === atom.field)

    if (atom.operator === CustomFieldQueryOperator.Exists || atom.operator === CustomFieldQueryOperator.IsNull) {
      return (
        <Form.Select
          size="sm"
          value={atom.value === 'true' || atom.value === true ? 'true' : 'false'}
          onChange={(e) => onValueChange(e.target.value)}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
        >
          <option value="true">{t('true')}</option>
          <option value="false">{t('false')}</option>
        </Form.Select>
      )
    }

    if (!field) {
      return (
        <Form.Control size="sm" type="text" value={atom.value || ''} onChange={(e) => onValueChange(e.target.value)} placeholder={t('enter_value')} />
      )
    }

    // Boolean field - true/false select
    if (field.data_type === CustomFieldDataType.Boolean) {
      return (
        <Form.Select
          size="sm"
          value={atom.value === 'true' || atom.value === true || atom.value === '1' ? 'true' : 'false'}
          onChange={(e) => onValueChange(e.target.value)}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
        >
          <option value="">{t('select_value')}</option>
          <option value="true">{t('true')}</option>
          <option value="false">{t('false')}</option>
        </Form.Select>
      )
    }

    // Date field - date picker
    if (field.data_type === CustomFieldDataType.Date) {
      return <Form.Control size="sm" type="date" value={atom.value || ''} onChange={(e) => onValueChange(e.target.value)} />
    }

    // Select field with 'In' operator - multiple select
    if (atom.operator === CustomFieldQueryOperator.In && field.data_type === CustomFieldDataType.Select) {
      return (
        <Form.Select
          size="sm"
          multiple
          value={Array.isArray(atom.value) ? atom.value : []}
          onChange={(e) => {
            const values = Array.from(e.target.selectedOptions, (option) => option.value)
            onValueChange(values)
          }}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
        >
          {field.extra_data?.select_options?.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )
    }

    // Select field with other operators - single select
    if (field.data_type === CustomFieldDataType.Select) {
      return (
        <Form.Select
          size="sm"
          value={atom.value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          onMouseDown={(e) => {
            e.stopPropagation()
          }}
        >
          <option value="">{t('select_value')}</option>
          {field.extra_data?.select_options?.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )
    }

    // Integer field - number input
    if (field.data_type === CustomFieldDataType.Integer) {
      return (
        <Form.Control
          size="sm"
          type="number"
          step="1"
          value={atom.value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={t('enter_value')}
        />
      )
    }

    // Float/Monetary fields - number input with decimals
    if (field.data_type === CustomFieldDataType.Float || field.data_type === CustomFieldDataType.Monetary) {
      return (
        <Form.Control
          size="sm"
          type="number"
          step="0.01"
          value={atom.value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={t('enter_value')}
        />
      )
    }

    // DocumentLink field - document picker component
    if (field.data_type === CustomFieldDataType.DocumentLink) {
      // Ensure value is an array
      const docLinkValue = Array.isArray(atom.value) ? atom.value : atom.value ? [parseInt(String(atom.value))] : []
      return (
        <div style={{ minWidth: '200px' }}>
          <DocumentLinkInput value={docLinkValue} onChange={onValueChange} disabled={false} />
        </div>
      )
    }

    // Default - text input for String, Url, LongText, etc.
    return (
      <Form.Control size="sm" type="text" value={atom.value || ''} onChange={(e) => onValueChange(e.target.value)} placeholder={t('enter_value')} />
    )
  }

  const renderQueryList = () => {
    return (
      <div className="list-group list-group-flush">
        {localModel.queries.map((element, index) => (
          <div key={index} className="list-group-item px-0 d-flex flex-nowrap">
            {element.type === CustomFieldQueryElementType.Expression && element.expression ? (
              <div className="w-100">{renderExpressionGroup(element.expression, 0, `root_${index}`)}</div>
            ) : element.type === CustomFieldQueryElementType.Atom && element.atom ? (
              <div className="input-group input-group-sm w-100">
                <Form.Select
                  className="docdot-input-select"
                  value={element.atom.field || ''}
                  onChange={(e) => {
                    const newModel = new CustomFieldQueriesModel()
                    newModel.logicalOperator = localModel.logicalOperator
                    newModel.queries = localModel.queries.map((q, i) => {
                      if (i === index && q.type === CustomFieldQueryElementType.Atom && q.atom) {
                        const atom = { ...q.atom }
                        atom.field = parseInt(e.target.value)
                        const field = customFields.find((f) => f.id === atom.field)
                        if (field?.data_type === CustomFieldDataType.DocumentLink && !Array.isArray(atom.value)) {
                          atom.value = []
                        }
                        return { type: CustomFieldQueryElementType.Atom, atom }
                      }
                      return q
                    })
                    setLocalModel(newModel)
                    localModelRef.current = newModel
                    handleModelChange(newModel, true)
                  }}
                  onMouseDown={(e) => {
                    ;(e.nativeEvent as any).stopImmediatePropagation()
                  }}
                >
                  <option value="">{t('select_field')}</option>
                  {customFields.map((field) => (
                    <option key={field.id} value={field.id}>
                      {field.name}
                    </option>
                  ))}
                </Form.Select>

                <Form.Select
                  className="w-25"
                  value={element.atom.operator || ''}
                  onChange={(e) => {
                    const newModel = new CustomFieldQueriesModel()
                    newModel.logicalOperator = localModel.logicalOperator
                    newModel.queries = localModel.queries.map((q, i) => {
                      if (i === index && q.type === CustomFieldQueryElementType.Atom && q.atom) {
                        const atom = { ...q.atom }
                        atom.operator = e.target.value
                        const field = customFields.find((f) => f.id === atom.field)
                        if (atom.operator === CustomFieldQueryOperator.Exists || atom.operator === CustomFieldQueryOperator.IsNull) {
                          atom.value = 'true'
                        } else if (atom.operator === CustomFieldQueryOperator.In) {
                          atom.value = []
                        } else if (field?.data_type === CustomFieldDataType.DocumentLink) {
                          atom.value = []
                        } else {
                          atom.value = ''
                        }
                        return { type: CustomFieldQueryElementType.Atom, atom }
                      }
                      return q
                    })
                    setLocalModel(newModel)
                    localModelRef.current = newModel
                    handleModelChange(newModel, true)
                  }}
                  disabled={disabled}
                  onMouseDown={(e) => {
                    ;(e.nativeEvent as any).stopImmediatePropagation()
                  }}
                >
                  <option value="">{t('select_operator')}</option>
                  {getOperatorsForField(element.atom.field || 0).map((operator) => (
                    <option key={operator} value={operator}>
                      {getOperatorLabel(operator)}
                    </option>
                  ))}
                </Form.Select>

                {renderAtomValueInputForList(element.atom, (val) => {
                  const newModel = new CustomFieldQueriesModel()
                  newModel.logicalOperator = localModel.logicalOperator
                  newModel.queries = localModel.queries.map((q, i) => {
                    if (i === index && q.type === CustomFieldQueryElementType.Atom && q.atom) {
                      return { type: CustomFieldQueryElementType.Atom, atom: { ...q.atom, value: val } }
                    }
                    return q
                  })
                  setLocalModel(newModel)
                  localModelRef.current = newModel
                  const wasValid = localModel.isValid()
                  const isValid = newModel.isValid()
                  handleModelChange(newModel, isValid && !wasValid)
                })}

                <Button
                  variant="link"
                  size="sm"
                  className="text-danger pe-0"
                  type="button"
                  onClick={() => {
                    const newModel = new CustomFieldQueriesModel()
                    newModel.logicalOperator = localModel.logicalOperator
                    newModel.queries = localModel.queries.filter((_, i) => i !== index)
                    setLocalModel(newModel)
                    localModelRef.current = newModel
                    handleModelChange(newModel)
                  }}
                  disabled={disabled}
                >
                  <i className="bi bi-x-circle"></i>
                </Button>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    )
  }

  const renderAtomValueInputForList = (atom: CustomFieldQueryAtom, onValueChange: (value: any) => void) => {
    const field = customFields.find((f) => f.id === atom.field)

    if (atom.operator === CustomFieldQueryOperator.Exists || atom.operator === CustomFieldQueryOperator.IsNull) {
      return (
        <Form.Select
          className="w-25 rounded-end"
          value={atom.value === 'true' || atom.value === true ? 'true' : 'false'}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
        >
          <option value="true">{t('true')}</option>
          <option value="false">{t('false')}</option>
        </Form.Select>
      )
    }

    if (!field) {
      return (
        <Form.Control
          className="w-25 rounded-end"
          type="text"
          value={atom.value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
        />
      )
    }

    if (field.data_type === CustomFieldDataType.Date) {
      return (
        <Form.Control
          className="w-25 rounded-end"
          type="date"
          value={atom.value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
          placeholder="yyyy-mm-dd"
        />
      )
    }

    if (field.data_type === CustomFieldDataType.Float || field.data_type === CustomFieldDataType.Integer) {
      return (
        <Form.Control
          className="w-25 rounded-end"
          type="number"
          value={atom.value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
          step={field.data_type === CustomFieldDataType.Integer ? '1' : '0.01'}
        />
      )
    }

    if (field.data_type === CustomFieldDataType.Boolean) {
      return (
        <Form.Select
          className="w-25 rounded-end"
          value={atom.value === 'true' || atom.value === true || atom.value === '1' ? 'true' : 'false'}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
        >
          <option value="true">{t('true')}</option>
          <option value="false">{t('false')}</option>
        </Form.Select>
      )
    }

    if (field.data_type === CustomFieldDataType.Select) {
      if (atom.operator === CustomFieldQueryOperator.In) {
        return (
          <Form.Select
            className="docdot-input-select rounded-end"
            multiple
            value={Array.isArray(atom.value) ? atom.value : []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions, (option) => option.value)
              onValueChange(values)
            }}
            disabled={disabled}
            onMouseDown={(e) => {
              ;(e.nativeEvent as any).stopImmediatePropagation()
            }}
          >
            {field.extra_data?.select_options?.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        )
      }
      return (
        <Form.Select
          className="docdot-input-select rounded-end"
          value={atom.value || ''}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
          onMouseDown={(e) => {
            ;(e.nativeEvent as any).stopImmediatePropagation()
          }}
        >
          {field.extra_data?.select_options?.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      )
    }

    if (field.data_type === CustomFieldDataType.DocumentLink) {
      const docLinkValue = Array.isArray(atom.value) ? atom.value : atom.value ? [parseInt(String(atom.value))] : []
      return (
        <div className="w-25 form-select doc-link-select p-0" style={{ minWidth: '140px', maxWidth: '200px' }}>
          <DocumentLinkInput value={docLinkValue} onChange={onValueChange} disabled={disabled} />
        </div>
      )
    }

    return (
      <Form.Control
        className="w-25 rounded-end"
        type="text"
        value={atom.value || ''}
        onChange={(e) => onValueChange(e.target.value)}
        disabled={disabled}
      />
    )
  }

  if (!useDropdown) {
    return renderQueryList()
  }

  const rootClassName = ['btn-group', isMobileViewport ? 'dropdown' : 'dropend', 'position-relative', fullWidth ? 'w-100' : '', className]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={rootClassName} ref={dropdownRef}>
      <Button
        variant={isActive ? 'primary' : 'outline-primary'}
        size="sm"
        onClick={(e) => {
          e.stopPropagation()
          if (show && debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
          }
          if (show) {
            const currentModel = localModelRef.current
            if (currentModel.isEmpty() || currentModel.isValid()) {
              isEditingRef.current = false
              onSelectionModelChange(currentModel)
            }
          }
          if (!show) {
            updateMenuPosition()
          }
          setShow(!show)
        }}
        disabled={disabled}
        className={`${fullWidth ? 'w-100 ' : ''}dropdown-toggle d-flex align-items-center`}
        style={{ whiteSpace: 'nowrap' }}
      >
        <span className="d-flex align-items-center gap-2 flex-grow-1">
          <span className="filter-chip__label">
            <i className={`bi bi-${icon}`}></i>
            <span>{title}</span>
          </span>
          {isActive && <span className="badge bg-primary ms-auto">{localModel.queries.length}</span>}
        </span>
      </Button>

      {show &&
        menuContainer &&
        createPortal(
          <div
            className="filter-panel-overlay"
            onClick={() => {
              if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
                debounceTimerRef.current = null
              }
              const currentModel = localModelRef.current
              if (currentModel.isEmpty() || currentModel.isValid()) {
                isEditingRef.current = false
                onSelectionModelChange(currentModel)
              }
              setShow(false)
            }}
          ></div>,
          menuContainer
        )}

      {show &&
        menuContainer &&
        createPortal(
          <div
            ref={menuRef}
            className="custom-fields-query-dropdown shadow"
            style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width, position: 'absolute', zIndex: 1100 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-3">
              <div
                className="d-flex justify-content-between align-items-center mb-3 pb-2"
                style={{ borderBottom: `1px solid var(--bs-border-color)` }}
              >
                <h6 className="mb-0">{t('custom_field_queries')}</h6>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddQuery}
                    disabled={customFields.length === 0}
                    title={t('add_condition')}
                    className="rounded-circle"
                    style={{ width: '32px', height: '32px', padding: 0 }}
                  >
                    <i className="bi bi-plus-circle"></i>
                  </Button>
                  <Button
                    variant="outline-warning"
                    size="sm"
                    onClick={() => {
                      const currentModel = localModelRef.current
                      const newModel = new CustomFieldQueriesModel()
                      newModel.logicalOperator = currentModel.logicalOperator
                      newModel.queries = [
                        ...currentModel.queries,
                        {
                          type: CustomFieldQueryElementType.Expression,
                          expression: {
                            operator: currentModel.logicalOperator,
                            elements: [],
                          },
                        },
                      ]
                      handleModelChange(newModel)
                    }}
                    disabled={customFields.length === 0}
                    title={t('add_expression_group')}
                    style={{ width: '32px', height: '32px', padding: 0, fontFamily: 'monospace', fontSize: '1.2em' }}
                  >
                    {'{}'}
                  </Button>
                  {isActive && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => {
                        const newModel = new CustomFieldQueriesModel()
                        handleModelChange(newModel)
                      }}
                      title={t('clear_all')}
                      style={{ width: '32px', height: '32px', padding: 0 }}
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                  )}
                </div>
              </div>

              {renderQueryList()}
            </div>
          </div>,
          menuContainer
        )}
    </div>
  )
}

export default React.memo(CustomFieldsQueryDropdown, (prevProps, nextProps) => {
  if (prevProps.disabled !== nextProps.disabled) return false
  if (prevProps.allowedCustomFieldIds !== nextProps.allowedCustomFieldIds) return false
  if (prevProps.selectionModel.queries.length !== nextProps.selectionModel.queries.length) return false
  if (prevProps.selectionModel.logicalOperator !== nextProps.selectionModel.logicalOperator) return false
  return true
})
